import React, { useState } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Breadcrumbs,
    Link,
    Divider,
    Autocomplete,
    Avatar,
    Tooltip,
    Chip,
    Modal,
    useTheme,
    AutocompleteRenderGetTagProps, // Import this type for getTagProps
    // AutocompleteOwnerState, // Import this if needed for ownerState, though often not directly used by user
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import moment from "moment";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import { ErrorMessage, Form, Formik, getIn } from "formik";
import { Close } from "@mui/icons-material"; // AddCircleOutline, RemoveCircleOutline removed as Autocomplete handles adding/removing
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

const validationSchema = (cloudProvider?: string) => Yup.object().shape({
    clusterNamePrefix: Yup.string()
        .required("This field is required")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
    vpcProjectId: Yup.string()
        .required("This field is required")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
    network: Yup.string()
        .required("This field is required")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
    nodeSubnet: (cloudProvider === 'gcp' || cloudProvider === 'azure' )
        ? Yup.string()
            .required("This field is required")
            .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        : Yup.string().optional(),
    nodeSubnetRange: cloudProvider === 'gcp' 
        ? Yup.string()
            .required("This field is required")
        : Yup.string().optional(),
    secondaryPodRange: cloudProvider === 'gcp'
        ? Yup.string()
            .required("This field is required")
            .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        : Yup.string().optional(),
    secondaryServiceRange: cloudProvider === 'gcp'
        ? Yup.string()
            .required("This field is required")
            .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        : Yup.string().optional(),
    // MODIFIED: nodeSubnetRanges now applies to 'aws' AND 'azure'
    nodeSubnetRanges: cloudProvider === 'aws'
        ? Yup.array().of(
            Yup.string()
                .required("Node Subnet Range is required")
          ).min(1, "At least one Node Subnet Range is required")
        : Yup.array().optional(),
    dnsServiceIp: cloudProvider === 'azure'
        ? Yup.string()
            .required("This field is required")
        : Yup.string().optional(),

    // MODIFIED: Validates as a required string for Azure
    defaultNodePools: cloudProvider === 'azure'
        ? Yup.string()
            .required("This field is required")
        : Yup.string().optional(),
    
    // MODIFIED: Validates as an optional string for Azure
    userNodePools: cloudProvider === 'azure'
        ? Yup.string()
            .optional()
        : Yup.string().optional(),
    releaseChannel: Yup.string()
        .required("This field is required")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
    kubernetesVersion: Yup.string()
        .required("This field is required"),
    reasonForRejection: Yup.string().when('isRejectingForm', {
        is: true,
        then: (schema) => schema.required("This field is required"),
        otherwise: (schema) => schema.optional(),
    }),
    isRejectingForm: Yup.boolean().optional(),
});

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string()
        .required("This field is required"),
})

const gkeKubernetesVersionList: any = {
    Stable: ["1.29.13-gke.1006000", "1.30.8-gke.1261000","1.30.9-gke.1009000","1.31.5-gke.1023000"],
    Rapid: ["1.28.15-gke.1388000","1.29.11-gke.1045000","1.30.7-gke.1136000","1.31.3-gke.1121001","1.31.3-gke.1162000"],
    Regular: ["1.28.15-gke.1342000","1.29.10-gke.1280000","1.30.6-gke.1596000","1.30.6-gke.1125000"],
    All: [
        "1.29.13-gke.1006000", "1.28.15-gke.1342000", "1.28.15-gke.1388000", "1.29.11-gke.1045000",
        "1.29.10-gke.1280000", "1.30.6-gke.1125000", "1.30.7-gke.1136000", "1.30.6-gke.1596000",
        "1.30.8-gke.1261000", "1.30.9-gke.1009000", "1.31.3-gke.1121001", "1.31.1-gke.1162000",
        "1.31.5-gke.1023000",
    ],
};

const eksKubernetesVersionList = {
    // EKS versions are typically like '1.29', '1.28'.
    // Mapping to GKE-like channels for UI consistency, adjust as needed.
    Stable: ["1.29", "1.28"], 
    Regular: ["1.28", "1.27"], 
    Rapid: ["1.30"], // Example: newest versions in "Rapid"
    All: ["1.30", "1.29", "1.28", "1.27", "1.26"]
};

// Assuming Azure Kubernetes Service (AKS) might have similar release channels or versions
// For now, mirroring EKS or GKE structure as per "same as AWS just that change the Labels"
const aksKubernetesVersionList = {
    Stable: ["1.33","1.32","1.31","1.30", "1.29", "1.28", "1.27"], 
    Regular: ["1.28", "1.27"], 
    Rapid: ["1.30"], // Example: newest versions in "Rapid"
    All: ["1.33","1.32","1.31","1.30", "1.29", "1.28", "1.27"]
};


const releaseChannelList = ["Rapid", "Regular", "Stable"];

const ClusterApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const [cluster, setCluster] = useState<any>({});
    const { user } = useAuth();
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [resourceStatus, setResourceStatus] = useState<any>({});
    const { id } = useParams();

    React.useEffect(()=>{
        const fetchData = async () =>{
            try{
                const data = await queryGraphql(`query ResourceStatus($resourceStatusId: String!) {
                    resourceStatus(id: $resourceStatusId) {
                        id
                        resourceId 
                        resourceName
                        currentStatus
                        resourceType
                        resourceEnvironment
                        resourceOwner { id name }
                        projectUrl
                        gcpClusterUrl
                        argoCdStaticUrl
                        subGroupProjectAppUrl
                        subGroupProjectInfraUrl
                        createdAt
                        updatedAt
                    }
                }`, {resourceStatusId: id});
                if(data.resourceStatus){
                    setResourceStatus(data.resourceStatus);
                    if (data.resourceStatus.resourceId) {
                        fetchClusterData(data.resourceStatus.resourceId);
                    }
                }
            }
            catch(error){
                console.log(error);
            }
        }
        if(id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[id]);


    const fetchClusterData = async (clusterId: string) => {
        try {
            const data = await queryGraphql(
                `
                    query($clusterId: String!){
                    cluster(id:$clusterId) {
                    id,
                    cloudProvider, 
                    clusterName,
                    clusterOwner{ id, name, googleProfilePicture },
                    gcpProjectId,
                    environment,
                    region,
                    clusterSize,
                    clusterType,
                    gcpClusterViewers,
                    clusterCreationType,
                    applicationRequesters,
                    costLabelPairs{ cost, label },
                    status,
                    createdAt,
                    actionedAt,
                    clusterNamePrefix,
                    vpcProjectId, 
                    network,      
                    nodeSubnet,   
                    nodeSubnetRange, 
                    nodeSubnetRanges,
                    secondaryPodRange, 
                    secondaryServiceRange, 
                    releaseChannel,
                    kubernetesVersion,
                    reasonForRejection
                    }
                    }
`,
                { clusterId: clusterId }
            );
            if (data.cluster) {
                setCluster(data.cluster);
            }
        } catch (error) {
            console.log(error);
        }
    };


    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
                <MiniBot/>
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/requests/clusters">
                        Requests
                    </Link>
                    <Typography color="text.primary">Request Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton
                        edge="start"
                        aria-label="back"
                        color="inherit"
                        onClick={() => navigate("/requests/clusters")}
                    >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">
                        Request Details
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={0.5} />
                <Grid item xs={11}>
                    {cluster.id && ( 
                        <Formik
                            initialValues={{
                                clusterNamePrefix: cluster.clusterCreationType === "existing" ? `${cluster.clusterName}` : `${cluster.clusterName}-${cluster.environment}`,
                                vpcProjectId: "", 
                                network: "",      
                                nodeSubnet: cluster.cloudProvider === 'gcp' ? "" :"", 
                                nodeSubnetRange: cluster.cloudProvider === 'gcp' ? "" :"", 
                                secondaryPodRange: "",
                                secondaryServiceRange: "",
                                // MODIFIED: Initialize nodeSubnetRanges for AWS and Azure
                                nodeSubnetRanges: cluster.cloudProvider === 'aws' ? [] : [], 
                                releaseChannel:  cluster.clusterCreationType === "existing" ? "stable" : (cluster.releaseChannel || ""), 
                                kubernetesVersion: cluster.clusterCreationType === "existing" ? "default" : (cluster.kubernetesVersion || ""), 
                                reasonForRejection: "",
                                isRejectingForm: false,
                                dnsServiceIp: cluster.cloudProvider === 'azure' ? "" : "",
                                defaultNodePools: cluster.cloudProvider === 'azure' ? "" : "",
                                userNodePools: cluster.cloudProvider === 'azure' ? "" : "",
                            }}
                            validationSchema={validationSchema(cluster.cloudProvider)}
                            onSubmit={async (values) => {
                                const {
                                    clusterNamePrefix, vpcProjectId, network,
                                    releaseChannel , kubernetesVersion,
                                } = values;

                                const releaseChannelValue = cluster.clusterCreationType === "existing" ? "stable" : releaseChannel;
                                const kubernetesVersionValue = cluster.clusterCreationType === "existing" ? "default" : kubernetesVersion;
                                
                                let approvalInput: any = {
                                    id: cluster.id,
                                    clusterNamePrefix,
                                    vpcProjectId, 
                                    network,      
                                    releaseChannel: releaseChannelValue,
                                    kubernetesVersion: kubernetesVersionValue,
                                };

                                if (cluster.cloudProvider === 'gcp') {
                                    approvalInput = {
                                        ...approvalInput,
                                        nodeSubnet: values.nodeSubnet,
                                        nodeSubnetRange: values.nodeSubnetRange, 
                                        secondaryPodRange: values.secondaryPodRange,
                                        secondaryServiceRange: values.secondaryServiceRange,
                                    };
                                } else if (cluster.cloudProvider === 'aws') {
                                    approvalInput = {
                                        ...approvalInput,
                                        nodeSubnetRanges: values.nodeSubnetRanges, 
                                    };
                                } else if (cluster.cloudProvider === 'azure') { // NEW: Handle Azure submission
                                    approvalInput = {
                                        ...approvalInput,
                                        nodeSubnet: values.nodeSubnet,
                                        secondaryPodRange: values.secondaryPodRange,
                                        secondaryServiceRange: values.secondaryServiceRange,
                                        dnsServiceIp: values.dnsServiceIp,
                                        defaultNodePools: values.defaultNodePools,
                                        userNodePools: values.userNodePools
                                        // Same as AWS for this field
                                    };
                                }
                                
                                const data = await queryGraphql(
                                    `
                                    mutation ApproveCluster($input: ClusterApproveInput!) {
                                        approveCluster(input: $input) {
                                            id
                                        }
                                    }
                                    `,
                                    { input: approvalInput }
                                );

                                const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
                                    updateResourceStatus(input: $input) {
                                        id
                                    }
                                    }`, {
                                        input: {
                                          currentStatus: "active",
                                          resourceId: cluster.id 
                                        }
                                      });

                                if (data?.approveCluster?.id && info?.updateResourceStatus?.id) {
                                    navigate("/requests/clusters");
                                }
                            }}
                        >
                            {({
                                handleChange,
                                handleBlur,
                                values,
                                setFieldValue,
                                errors,
                                touched,
                                isValid,
                                dirty,
                            }) => {
                                return (
                                    <Form id="approver-main">
                                        <Box mx={4}>
                                            <Typography fontSize="16px" my={1}>
                                                Review the cluster and fill the cloud infra details
                                            </Typography>
                                            <Box>
                                                <Box
                                                    bgcolor={
                                                        cluster.status === "active"
                                                            ? "#12B76A10"
                                                            : cluster.status === "rejected"
                                                            ? "#F0443810"
                                                            : "#FDB02210"
                                                    }
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    display="flex"
                                                    borderRadius="12px 12px 0px 0px"
                                                    p={2}
                                                    boxShadow={1}
                                                >
                                                    <Box>
                                                        <Typography fontSize="14px">Cluster Details</Typography>
                                                        <Typography
                                                            fontFamily="Gilroy-Regular"
                                                            fontSize="12px"
                                                        >{`Requested By: ${cluster.clusterOwner?.name} | On: ${moment(
                                                            new Date(Number(cluster.createdAt)).toISOString()
                                                        ).format("DD/MM/YYYY")}`}</Typography>
                                                    </Box>
                                                    <Box alignItems="center">
                                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                                            <img
                                                                src={
                                                                    cluster.status === "active"
                                                                        ? "/assets/tickcircle.svg"
                                                                        : cluster.status === "rejected"
                                                                        ? "/assets/reject.svg"
                                                                        : "/assets/clock.svg"
                                                                }
                                                                alt="Icon"
                                                                style={{ width: "20px", marginRight: "8px" }}
                                                            />
                                                            <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                                {cluster.status === "active"
                                                                    ? "Active"
                                                                    : cluster.status === "rejected"
                                                                    ? "Rejected"
                                                                    : "Pending Approval"}
                                                            </Typography>
                                                        </Box>
                                                        {(cluster.status === "active" || cluster.status === "rejected") && (
                                                            <Typography
                                                                fontSize="12px"
                                                                fontFamily="Gilroy-Regular"
                                                            >{`On: ${moment(
                                                                new Date(Number(cluster.actionedAt)).toISOString()
                                                            ).format("DD/MM/YYYY")}`}</Typography>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Box bgcolor="#fff" p={2} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12} sm={2.4}>
                                                            <Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster Name</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.clusterName}</Typography></Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={2.4}>
                                                            <Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cloud Provider</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{textTransform: 'uppercase'}}>{cluster.cloudProvider || 'N/A'}</Typography></Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={2.4}>
                                                            <Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">
                                                                {/* MODIFIED: Label for VPC/Project ID */}
                                                                {cluster.cloudProvider === 'aws' ? 'AWS Account ID' : cluster.cloudProvider === 'azure' ? 'Azure Subscription ID' : 'GCP Project ID'}
                                                                </Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.gcpProjectId}</Typography></Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={2.4}>
                                                            <Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Environment</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.environment}</Typography></Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={2.4}>
                                                             <Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Region </Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.region}</Typography></Box>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container mt={2} spacing={1}>
                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster Owner</Typography><Box display="flex" alignItems="center" justifyContent="flex-start"><Avatar sx={{ height: "20px", width: "20px", marginRight: 0.5 }} src={cluster.clusterOwner?.googleProfilePicture} /><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.clusterOwner?.name}</Typography></Box></Box></Grid>
                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster Size</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.clusterSize}</Typography></Box></Grid>
                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster Type</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.clusterType}</Typography></Box></Grid>
                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster Viewers</Typography><Box display="flex" alignItems="center" justifyContent="flex-start">{cluster.gcpClusterViewers?.map((e: string, idx: number) => (<Tooltip key={idx} title={e}><Avatar sx={{ height: "20px", width: "20px", marginRight: 0.5 }} src={e} alt={e} /></Tooltip>))}</Box></Box></Grid>
                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Application Owners</Typography><Box display="flex" alignItems="center" justifyContent="flex-start">{cluster.applicationRequesters?.map((e: string, idx: number) => (<Tooltip key={idx} title={e}><Avatar sx={{ height: "20px", width: "20px", marginRight: 0.5 }} src={e} alt={e} /></Tooltip>))}</Box></Box></Grid>
                                                    </Grid>
                                                    <Box><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="12px" mt={2}>Tags/ Labels</Typography><Box ml={-0.5}>{cluster.costLabelPairs?.map((e: any, idx: number) => (<Chip key={idx} size="small" sx={{ bgcolor: "#10101010", border: "1px solid black", mr: 0.75, fontFamily: "Gilroy-Regular", fontSize: "12px" }} variant="filled" label={`${e.cost} • ${e.label}`} />))}</Box></Box>
                                                </Box>

                                                {cluster.status === "pending" &&
                                                    user.roles.some((element: string) =>
                                                        ["infrastructure_admin", "viewer"].includes(element) 
                                                    ) && (
                                                        <Box mt={2} bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1}>
                                                            <Typography fontSize="14px">Cloud Infra Details ({cluster.cloudProvider?.toUpperCase()})</Typography>
                                                            <Divider sx={{ marginY: 2 }} />
                                                            <Grid container gap={2}>
                                                                <Grid item xs={12} sm={5.75}>
                                                                    <CompulsoryFieldsLabel fieldName="Cluster Name Prefix" />
                                                                    <TextField fullWidth disabled name="clusterNamePrefix" value={values.clusterNamePrefix} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["clusterNamePrefix"] && touched["clusterNamePrefix"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}} />
                                                                    <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterNamePrefix" component="div" /></Box>
                                                                </Grid>
                                                                <Grid item xs={12} sm={5.75}>
                                                                    <CompulsoryFieldsLabel fieldName={
                                                                        // MODIFIED: Label for VPC/Project ID for input
                                                                        cluster.cloudProvider === 'aws'
                                                                            ? "VPC-AWS Account ID"
                                                                            : cluster.cloudProvider === 'azure'
                                                                                ? "VPC-Azure Subscription ID" // New label for Azure
                                                                                : "VPC-GCP Project ID"
                                                                    } />
                                                                    <TextField fullWidth name="vpcProjectId" value={values.vpcProjectId} onChange={handleChange} onBlur={handleBlur} placeholder="Enter ID here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["vpcProjectId"] && touched["vpcProjectId"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                    <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="vpcProjectId" component="div" /></Box>
                                                                </Grid>
                                                                <Grid item xs={12} sm={5.75}>
                                                                    <CompulsoryFieldsLabel fieldName={
                                                                        // MODIFIED: Label for Network/VPC ID for input
                                                                        cluster.cloudProvider === 'aws'
                                                                            ? "VPC ID"
                                                                            : cluster.cloudProvider === 'azure'
                                                                                ? "Virtual Network Name" // New label for Azure
                                                                                : "Network"
                                                                    } />
                                                                    <TextField fullWidth name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name/ID here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["network"] && touched["network"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}} />
                                                                    <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="network" component="div" /></Box>
                                                                </Grid>

                                                                {cluster.cloudProvider === 'gcp' && (
                                                                    <>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Node Subnet" />
                                                                            <TextField fullWidth name="nodeSubnet" value={values.nodeSubnet} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["nodeSubnet"] && touched["nodeSubnet"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="nodeSubnet" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Master CIDR Range" />
                                                                            <TextField fullWidth name="nodeSubnetRange" value={values.nodeSubnetRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter CIDR here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["nodeSubnetRange"] && touched["nodeSubnetRange"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="nodeSubnetRange" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Secondary Range Name for Pods" />
                                                                            <TextField fullWidth name="secondaryPodRange" value={values.secondaryPodRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["secondaryPodRange"] && touched["secondaryPodRange"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="secondaryPodRange" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Secondary Range Name for Services" />
                                                                            <TextField fullWidth name="secondaryServiceRange" value={values.secondaryServiceRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["secondaryServiceRange"] && touched["secondaryServiceRange"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="secondaryServiceRange" component="div" /></Box>
                                                                        </Grid>
                                                                    </>
                                                                )}

                                                                {/* AWS Node Subnet Ranges */}
                                                                {cluster.cloudProvider === 'aws' && (
                                                                    <Grid item xs={12} sm={5.75}> 
                                                                        <CompulsoryFieldsLabel fieldName="Node Subnet Ranges" />
                                                                        {/* Explicitly typing Autocomplete component */}
                                                                        <Autocomplete<string, true, false, true>
                                                                            multiple
                                                                            freeSolo 
                                                                            options={[] as string[]} // Provide explicitly typed empty array for options
                                                                            value={values.nodeSubnetRanges as string[]} // Assert value type
                                                                            onChange={(event, newValue: string[]) => { // Assert newValue type
                                                                                setFieldValue("nodeSubnetRanges", newValue);
                                                                            }}
                                                                            onBlur={() => { 
                                                                                handleBlur({ target: { name: "nodeSubnetRanges" } });
                                                                            }}
                                                                            ChipProps={{ size: 'small' }}
                                                                            renderTags={(value: readonly string[], getTagProps: AutocompleteRenderGetTagProps) => // Type annotation for renderTags parameters
                                                                                value.map((option: string, index: number) => (
                                                                                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                                                                                ))
                                                                            }
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    variant="filled"
                                                                                    name="nodeSubnetRanges_input" 
                                                                                    placeholder="Type subnet ID or CIDR and press Enter"
                                                                                    hiddenLabel
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        disableUnderline: true,
                                                                                        style: { borderRadius: "8px" },
                                                                                    }}
                                                                                    sx={{ "& .MuiFilledInput-root": { 
                                                                                        border: getIn(errors, 'nodeSubnetRanges') && getIn(touched, 'nodeSubnetRanges') ? "1px solid red" : "1px solid #00000000", 
                                                                                        "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }
                                                                                    }}}
                                                                                />
                                                                            )}
                                                                        />
                                                                        <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                                            <ErrorMessage name="nodeSubnetRanges" component="div" />
                                                                        </Box>
                                                                    </Grid>
                                                                )}

                                                                {/* NEW: Azure Node Subnet Ranges (same as AWS functionality, different label hints if needed) */}
                                                                {cluster.cloudProvider === 'azure' && (
                                                                    <>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Node Subnet" />
                                                                            <TextField fullWidth name="nodeSubnet" value={values.nodeSubnet} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["nodeSubnet"] && touched["nodeSubnet"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="nodeSubnet" component="div" /></Box>
                                                                        </Grid>
                                                                        {/* <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Master CIDR Range" />
                                                                            <TextField fullWidth name="nodeSubnetRange" value={values.nodeSubnetRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter CIDR here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["nodeSubnetRange"] && touched["nodeSubnetRange"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="nodeSubnetRange" component="div" /></Box>
                                                                        </Grid> */}
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Secondary Range for Pods" />
                                                                            <TextField fullWidth name="secondaryPodRange" value={values.secondaryPodRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["secondaryPodRange"] && touched["secondaryPodRange"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="secondaryPodRange" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Secondary Range for Services" />
                                                                            <TextField fullWidth name="secondaryServiceRange" value={values.secondaryServiceRange} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["secondaryServiceRange"] && touched["secondaryServiceRange"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="secondaryServiceRange" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="DNS Service IP" />
                                                                            <TextField fullWidth name="dnsServiceIp" value={values.dnsServiceIp} onChange={handleChange} onBlur={handleBlur} placeholder="Enter DNS Server IP" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["dnsServiceIp"] && touched["dnsServiceIp"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="dnsServiceIp" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="Default Node Pools" />
                                                                            <TextField fullWidth name="defaultNodePools" value={values.defaultNodePools} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Default Node Pools here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["defaultNodePools"] && touched["defaultNodePools"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="defaultNodePools" component="div" /></Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={5.75}>
                                                                            <CompulsoryFieldsLabel fieldName="User Node Pools" />
                                                                            <TextField fullWidth name="userNodePools" value={values.userNodePools} onChange={handleChange} onBlur={handleBlur} placeholder="Enter User Node Pools here" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["userNodePools"] && touched["userNodePools"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}/>
                                                                            <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="userNodePools" component="div" /></Box>
                                                                        </Grid>
                                                                    </>
                                                                )}


                                                                {cluster.clusterCreationType !== "existing" && (
                                                                <>
                                                                <Grid item xs={12} sm={5.75}>
                                                                    <CompulsoryFieldsLabel fieldName="Release Channel" />
                                                                    <TextField select placeholder="Select" name="releaseChannel" value={values.releaseChannel} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} sx={{ "& .MuiFilledInput-root": { border: errors["releaseChannel"] && touched["releaseChannel"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}}>
                                                                        {releaseChannelList.map((e) => (<MenuItem key={e} value={e} onClick={() => setFieldValue("releaseChannel", e)}>{e}</MenuItem>))}
                                                                    </TextField>
                                                                    <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="releaseChannel" component="div" /></Box>
                                                                </Grid>
                                                                <Grid item xs={12} sm={5.75}>
                                                                    <Box sx={{display:'flex', alignItems: 'center'}}><CompulsoryFieldsLabel fieldName="Kubernetes Version" />
                                                                    {cluster.cloudProvider === 'gcp' ? 
                                                                    <Tooltip title="Refer to GKE release notes for available versions."><Avatar src="/assets/info.svg" alt="info" sx={{ height: 18, width: 18, ml: 0.5, cursor: 'pointer'}} onClick= {() => {window.open('https://cloud.google.com/kubernetes-engine/docs/release-notes-stable', '_blank')}} />
                                                                    </Tooltip> : cluster.cloudProvider === 'aws' ?
                                                                    <Tooltip title="Refer to EKS release notes for available versions."><Avatar src="/assets/info.svg" alt="info" sx={{ height: 18, width: 18, ml: 0.5, cursor: 'pointer'}} onClick= {() => {window.open('https://docs.aws.amazon.com/eks/latest/userguide/kubernetes-versions.html', '_blank')}} />
                                                                    </Tooltip> : // NEW: Azure tooltip
                                                                    <Tooltip title="Refer to AKS release notes for available versions."><Avatar src="/assets/info.svg" alt="info" sx={{ height: 18, width: 18, ml: 0.5, cursor: 'pointer'}} onClick= {() => {window.open('https://learn.microsoft.com/en-us/azure/aks/supported-kubernetes-versions', '_blank')}} />
                                                                    </Tooltip>}
                                                                    </Box>
                                                                    <Autocomplete
                                                                        freeSolo
                                                                        onChange={(event, newValue) => { setFieldValue('kubernetesVersion', newValue || ""); }} 
                                                                        value={values.kubernetesVersion}
                                                                        options={(() => {
    const provider = cluster.cloudProvider; // 'gcp' or 'aws' or 'azure'
    const currentChannel = values.releaseChannel;
    let versionListSource;

    if (provider === 'aws') {
        versionListSource = eksKubernetesVersionList;
    } else if (provider === 'azure') { // NEW: Use AKS list for Azure
        versionListSource = aksKubernetesVersionList;
    }
    else { // Default to GKE or if provider is 'gcp'
        versionListSource = gkeKubernetesVersionList;
    }

    if (currentChannel && versionListSource[currentChannel as keyof typeof versionListSource]) {
        return versionListSource[currentChannel as keyof typeof versionListSource];
    }
    return versionListSource.All || [];
})()}
                                                                        filterSelectedOptions
                                                                        renderInput={(params) => (
                                                                            <TextField {...params} name="kubernetesVersion" onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth placeholder="Enter or select Kubernetes version" size="medium" hiddenLabel sx={{ "& .MuiFilledInput-root": { border: errors["kubernetesVersion"] && touched["kubernetesVersion"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}} InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />
                                                                        )}
                                                                    />
                                                                    <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="kubernetesVersion" component="div" /></Box>
                                                                </Grid>
                                                                </>
                                                                )}
                                                            </Grid>
                                                        </Box>
                                                    )}
                                            </Box>
                                            {(cluster.status === "active" || cluster.status === "rejected") && (
                                                <Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
                                                    <Typography fontSize="14px">Cloud Infra Details ({cluster.cloudProvider?.toUpperCase()})</Typography>
                                                    <Divider sx={{ my: 2 }} />
                                                    {cluster.status === "rejected" && (
                                                        <Grid container><Grid item xs={12}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Reason for rejection</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.reasonForRejection}</Typography></Box></Grid></Grid>
                                                    )}
                                                    {cluster.status === "active" && (
                                                        <>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster Name Prefix</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.clusterNamePrefix}</Typography></Box></Grid>
                                                                <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">
                                                                    {/* MODIFIED: Label for VPC/Project ID for display */}
                                                                    {cluster.cloudProvider === 'aws' ? "VPC-AWS Account ID" : cluster.cloudProvider === 'azure' ? "Azure Subscription ID" : "VPC-GCP Project ID"}
                                                                </Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.vpcProjectId}</Typography></Box></Grid>
                                                                <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">
                                                                    {/* MODIFIED: Label for Network/VPC ID for display */}
                                                                    {cluster.cloudProvider === 'aws' ? "VPC ID" : cluster.cloudProvider === 'azure' ? "Virtual Network Name" : "Network"}
                                                                </Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.network}</Typography></Box></Grid>
                                                                
                                                                {cluster.cloudProvider === 'gcp' && (
                                                                    <>
                                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Node Subnet</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.nodeSubnet}</Typography></Box></Grid>
                                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Master CIDR range</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.nodeSubnetRange}</Typography></Box></Grid>
                                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Secondary Range Name for Pods</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.secondaryPodRange}</Typography></Box></Grid>
                                                                        <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Secondary Range Name for Services</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.secondaryServiceRange}</Typography></Box></Grid>
                                                                    </>
                                                                )}
                                                                {/* MODIFIED: Ensure nodeSubnetRanges is used consistently for AWS display */}
                                                                {cluster.cloudProvider === 'aws' && Array.isArray(cluster.nodeSubnetRanges) && (
                                                                    <Grid item xs={12} sm={4.8}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Node Subnet Ranges</Typography>{cluster.nodeSubnetRanges.map((subnet: string, i: number) => (<Typography key={i} fontFamily="Gilroy-Semibold" fontSize="14px">{subnet}</Typography>))}</Box></Grid>
                                                                )}
                                                                {/* NEW: Azure Node Subnet Ranges display */}
                                                                {cluster.cloudProvider === 'azure' && Array.isArray(cluster.nodeSubnetRanges) && (
                                                                    <Grid item xs={12} sm={4.8}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Node Subnet Ranges</Typography>{cluster.nodeSubnetRanges.map((subnet: string, i: number) => (<Typography key={i} fontFamily="Gilroy-Semibold" fontSize="14px">{subnet}</Typography>))}</Box></Grid>
                                                                )}

                                                                <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Release Channel</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.releaseChannel}</Typography></Box></Grid>
                                                                <Grid item xs={12} sm={2.4}><Box><Typography fontFamily="Gilroy-Regular" fontSize="12px">Kubernetes Version</Typography><Typography fontFamily="Gilroy-Semibold" fontSize="14px">{cluster.kubernetesVersion}</Typography></Box></Grid>
                                                                <Grid item xs={12} sm={2.4}>
                                                                    <Box>
                                                                        <Typography fontFamily="Gilroy-Regular" fontSize="12px">Infra URLs</Typography>
                                                                        <Box display="flex" alignItems="center" justifyContent="flex-start">
                                                                            <Typography fontFamily="Gilroy-SemiBold" fontSize={14} sx={{ display:"flex", alignItems: "center"}}>
                                                                                { (resourceStatus.gcpClusterUrl || resourceStatus.projectUrl || resourceStatus.argoCdStaticUrl || resourceStatus.subGroupProjectAppUrl || resourceStatus.subGroupProjectInfraUrl) ? Object.values(resourceStatus).filter(val => typeof val === 'string' && val.startsWith('http')).length : '0' }
                                                                                <Tooltip 
                                                                                    sx={{ whiteSpace: "pre-line" }}
                                                                                    title={
                                                                                        <>
                                                                                        <Typography fontFamily="Gilroy-Bold" fontSize="14px" color="common.white" gutterBottom>List of Infra URLs</Typography>
                                                                                        {resourceStatus.resourceId === cluster.id && resourceStatus.gcpClusterUrl && (<Link href={resourceStatus.gcpClusterUrl} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'common.white', display:'block' }}><Typography fontFamily="Gilroy-Regular" fontSize="12px">Cluster URL</Typography></Link>)}
                                                                                        {resourceStatus.resourceId === cluster.id && resourceStatus.projectUrl && (<Link href={resourceStatus.projectUrl} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'common.white', display:'block' }}><Typography fontFamily="Gilroy-Regular" fontSize="12px">Project URL</Typography></Link>)}
                                                                                        {resourceStatus.resourceId === cluster.id && resourceStatus.argoCdStaticUrl && (<Link href={resourceStatus.argoCdStaticUrl} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'common.white', display:'block' }}><Typography fontFamily="Gilroy-Regular" fontSize="12px">ArgoCD URL</Typography></Link>)}
                                                                                        {resourceStatus.resourceId === cluster.id && resourceStatus.subGroupProjectAppUrl && (<Link href={resourceStatus.subGroupProjectAppUrl} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'common.white', display:'block' }}><Typography fontFamily="Gilroy-Regular" fontSize="12px">Sub Group Project App URL</Typography></Link>)}
                                                                                        {resourceStatus.resourceId === cluster.id && resourceStatus.subGroupProjectInfraUrl && (<Link href={resourceStatus.subGroupProjectInfraUrl} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'common.white', display:'block' }}><Typography fontFamily="Gilroy-Regular" fontSize="12px">Sub Group Project Infra URL</Typography></Link>)}
                                                                                        {!resourceStatus.gcpClusterUrl && !resourceStatus.projectUrl && !resourceStatus.argoCdStaticUrl && !resourceStatus.subGroupProjectAppUrl && !resourceStatus.subGroupProjectInfraUrl && (<Typography fontFamily="Gilroy-Regular" fontSize="12px" color="common.white">No URLs available</Typography>)}
                                                                                        </>
                                                                                    }
                                                                                    slotProps={{ tooltip: { sx: { bgcolor: '#333333', p: 1.5, borderRadius: '4px' } } }}
                                                                                >
                                                                                    <Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, marginLeft: "4px", cursor: 'pointer' }} />
                                                                                </Tooltip>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Box>
                                            )}

                                            {cluster.status === "pending" &&
                                                user.roles.some((element: string) =>
                                                    ["infrastructure_admin"].includes(element)
                                                ) && (
                                                    <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
                                                        <Button sx={{color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="outlined" size="large" onClick={() => {setOpenRejectModal(true);}}>Reject Request</Button>
                                                        <Button 
                                                            sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
                                                            variant="contained" 
                                                            size="large" 
                                                            disabled={!isValid || (!dirty && !Object.values(values).some(v => {
                                                                if (values.isRejectingForm) return false; 
                                                                if (Array.isArray(v)) { 
                                                                    // For awsNodeSubnetRanges (string[]), check if any item is a non-empty string
                                                                    return v.length > 0 && v.some(item => typeof item === 'string' && item !== "");
                                                                }
                                                                if (typeof v === 'boolean' && Object.keys(values).find(key => values[key as keyof typeof values] === v && key === 'isRejectingForm')) return false; 
                                                                return typeof v === 'string' && v !== "";
                                                            }))}
                                                            onClick={() => {setOpenApproveModal(true);}}>Approve Request</Button>
                                                    </Box>
                                                )}
                                            
                                            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)} >
                                                <Box sx={{position: "absolute",top: "50%",left: "50%",transform: "translate(-50%, -50%)",width: 700,bgcolor: "white",border: "0px",boxShadow: 2,borderRadius: "16px",}}>
                                                    <Box display="flex" justifyContent="space-between"><Typography fontFamily="Gilroy-SemiBold" fontSize="20px" p={2}>Approve</Typography><IconButton edge="start" aria-label="back" color="inherit" onClick={() => setOpenApproveModal(false)} sx={{ m: 2 }}><Close fontSize="small" /></IconButton></Box>
                                                    <Divider />
                                                    <Typography fontSize="16px" p={2}>Are you sure you want to approve the request?</Typography>
                                                    <Typography fontSize="16px" p={2}>On approving request will be approved and Kubernetes cluster details will be added successful.</Typography>
                                                    <Divider />
                                                    <Box sx={{ m: 2, textAlign: "right" }}><Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black"},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="contained" size="small" type="submit" form="approver-main">Approve</Button></Box>
                                                </Box>
                                            </Modal>
                                        </Box>
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Grid>
                <Grid item xs={0.5} />
            </Grid>

            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)} >
                <Box sx={{position: "absolute",top: "50%",left: "50%",transform: "translate(-50%, -50%)",width: 700,bgcolor: "white",border: "0px",boxShadow: 2,borderRadius: "16px",}}>
                    <Formik
                        onSubmit={async ({ reasonForRejection }) => {
                            const data = await queryGraphql( `mutation RejectCluster($input: ClusterRejectInput!) { rejectCluster(input: $input) { id } }`, { input: { id: cluster.id, reasonForRejection,}});
                            const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) { updateResourceStatus(input: $input) { id } }`, { input: { currentStatus: "rejected", resourceId: cluster.id }});
                            if (data.rejectCluster.id && info.updateResourceStatus.id) { navigate("/requests/clusters");}
                        }}
                        validationSchema={rejectValidationSchema} 
                        initialValues={{ 
                            reasonForRejection: "", 
                            isRejectingForm: true, 
                             clusterNamePrefix: cluster.clusterCreationType === "existing" ? `${cluster.clusterName}` : `${cluster.clusterName}-${cluster.environment}`,
                                vpcProjectId: "", 
                                network: "",      
                                nodeSubnet: cluster.cloudProvider === 'gcp' ? "" :"", 
                                nodeSubnetRange: cluster.cloudProvider === 'gcp' ? "" :"", 
                                secondaryPodRange: "",
                                secondaryServiceRange: "",
                                // MODIFIED: Initialize for Azure in reject modal too
                                nodeSubnetRanges: (cluster.cloudProvider === 'aws' || cluster.cloudProvider === 'azure') ? [] : [], 
                                releaseChannel:  cluster.clusterCreationType === "existing" ? "stable" : (cluster.releaseChannel || ""), 
                                kubernetesVersion: cluster.clusterCreationType === "existing" ? "default" : (cluster.kubernetesVersion || ""), 
                               
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty}) => (
                                <Form id="reject-form">
                                    <Box display="flex" justifyContent="space-between"><Typography fontFamily="Gilroy-SemiBold" fontSize="20px" p={2}>Reject Request</Typography><IconButton edge="start" aria-label="back" color="inherit" onClick={() => setOpenRejectModal(false)} sx={{ m: 2 }}><Close fontSize="small" /></IconButton></Box>
                                    <Divider />
                                    <Typography fontSize="16px" p={2}>Add comments for the requestor explaining the reason of rejection</Typography>
                                    <Box p={2}>
                                        <CompulsoryFieldsLabel fieldName="Comments" />
                                        <TextField fullWidth multiline name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comment" size="medium" hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }}} sx={{ "& .MuiFilledInput-root": { border: errors["reasonForRejection"] && touched["reasonForRejection"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" }}}} />
                                        <Box sx={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="reasonForRejection" component="div" /></Box>
                                    </Box>
                                    <Box sx={{ m: 2, textAlign: "right" }}><Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="contained" size="small" type="submit" form="reject-form" disabled={!isValid || !dirty}>Reject</Button></Box>
                                </Form>
                            )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};


const ClusterApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-cluster"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: `${currentMarginLeft}vh`, }}>
                <ClusterApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default ClusterApproveFormPage;


// import React, { useState } from "react";
// import {
// 	Box,
// 	Button,
// 	Container,
// 	Grid,
// 	TextField,
// 	Typography,
// 	MenuItem,
// 	IconButton,
// 	Breadcrumbs,
// 	Link,
// 	Divider,
// 	Autocomplete,
// 	Avatar,
// 	Tooltip,
// 	Chip,
// 	Modal,
// 	InputProps,
// 	useTheme,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import * as Yup from "yup";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";
// import moment from "moment";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import { ErrorMessage, Form, Formik } from "formik";
// import { Close } from "@mui/icons-material";
// import MiniBot from "../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
// const validationSchema = Yup.object().shape({
// 	// 					releaseChannel: "",
// 	// 					kubernetesVersion: "",
// 	clusterNamePrefix: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.lowercase("This field must be in lowercase")
// 		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 		.max(30, "This field must not exceed 30 characters"),
// 	vpcProjectId: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.lowercase("This field must be in lowercase")
// 		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
// 	network: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.lowercase("This field must be in lowercase")
// 		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
// 	nodeSubnet: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.lowercase("This field must be in lowercase")
// 		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 		.max(30, "This field must not exceed 30 characters"),
// 	nodeSubnetRange: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.matches(
// 			/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(3[0-2]|[1-2]?[0-9])$/,
// 			"This field must be a valid ip subnet range"
// 		),
// 	// nodeSubnetRange: Yup.string()
//     // .required("This field is required")
//     // .strict()
//     // .matches(
//     //     /^[a-zA-Z]+$/,
//     //     "This field must contain only lowercase or uppercase letters"
//     // ),

// 	secondaryPodRange: Yup.string()
//     .required("This field is required")
//     .strict()
//     .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),


// 	// secondaryPodRange: Yup.string()
// 	// 	.required("This field is required")
// 	// 	.strict()
// 	// 	.matches(
// 	// 		/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(3[0-2]|[1-2]?[0-9])$/,
// 	// 		"This field must be a valid ip subnet range"
// 	// 	),

// 	secondaryServiceRange: Yup.string()
//     .required("This field is required")
//     .strict()
//     .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),

// 	// secondaryServiceRange: Yup.string()
// 	// 	.required("This field is required")
// 	// 	.strict()
// 	// 	.matches(
// 	// 		/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(3[0-2]|[1-2]?[0-9])$/,
// 	// 		"This field must be a valid ip subnet range"
// 	// 	),
// 	releaseChannel: Yup.string()
// 		.required("This field is required")
// 		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 		.strict(),
// 	kubernetesVersion: Yup.string()
// 		.required("This field is required")
// 		// .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 		.strict(),
// });

// const kubernetesVersionList: any = {
// 	Stable: ["1.29.13-gke.1006000", "1.30.8-gke.1261000","1.30.9-gke.1009000","1.31.5-gke.1023000"],
// 	Rapid: ["1.28.15-gke.1388000","1.29.11-gke.1045000","1.30.7-gke.1136000","1.31.3-gke.1121001","1.31.3-gke.1162000"],
// 	Regular: ["1.28.15-gke.1342000","1.29.10-gke.1280000","1.30.6-gke.1596000","1.30.6-gke.1125000"],
// 	All: [
// 		"1.29.13-gke.1006000",
// 		"1.28.15-gke.1342000",
// 		"1.28.15-gke.1388000",
// 		"1.29.11-gke.1045000",
// 		"1.29.10-gke.1280000",
// 		"1.30.6-gke.1125000",
// 		"1.30.7-gke.1136000",
// 		"1.30.6-gke.1596000",
// 		"1.30.8-gke.1261000",
// 		"1.30.9-gke.1009000",
// 		"1.31.3-gke.1121001",
// 		"1.31.1-gke.1162000",
// 		"1.31.5-gke.1023000",
// 	],
// };
// const releaseChannelList = ["Rapid", "Regular", "Stable"];

// const ClusterApproveFormPageContent: React.FC = () => {
// 	const navigate = useNavigate();
// 	const [cluster, setCluster] = useState<any>({});
// 	const { user } = useAuth();
// 	const [openRejectModal, setOpenRejectModal] = useState(false);
// 	const [openApproveModal, setOpenApproveModal] = useState(false);
// 	const [resourceStatus, setResourceStatus] = useState<any>({});

// 	React.useEffect(()=>{
// 		const fetchData = async () =>{
// 			try{
// 				const data = await queryGraphql(`query ResourceStatus($resourceStatusId: String!) {
//   resourceStatus(id: $resourceStatusId) {
//     id
//     resourceId
//     resourceName
//     currentStatus
//     resourceType
//     resourceEnvironment
//     resourceOwner {
//       id
//       name
//     }
//     projectUrl
//     gcpClusterUrl
//     argoCdStaticUrl
//     subGroupProjectAppUrl
//     subGroupProjectInfraUrl
//     createdAt
//     updatedAt
//   }
// }`, {resourceStatusId: id});
// if(data.resourceStatus){
// 	// console.dir(data.cluster);
// 	setResourceStatus(data.resourceStatus);
	
// }
// 			}
// 			catch(error){
// 				console.log(error);
// 			}
// 		}
// 		fetchData();
// 	},[]);


// 	const { id } = useParams();

// 	React.useEffect(() => {
// 		const fetchData = async () => {
// 			try {
// 				const data = await queryGraphql(
// 					`
//                         query($clusterId: String!){
//                         cluster(id:$clusterId) {
// 						id,
//                         clusterName,
//                         clusterOwner{
//                         id,
//                         name,
//                         googleProfilePicture
//                         },
//                         gcpProjectId,
//                         environment,
//                         region,
//                         clusterSize,
//                         clusterType,
//                         gcpClusterViewers,
// 						clusterCreationType,
//                         applicationRequesters,
//                         costLabelPairs{
//                         cost,
//                         label
//                         },
//                         status,
//                         createdAt,
// 						actionedAt,
// 						clusterNamePrefix,
// 						vpcProjectId,
// 						network,
// 						nodeSubnet,
// 						nodeSubnetRange,
// 						secondaryPodRange,
// 						secondaryServiceRange,
// 						releaseChannel,
// 						kubernetesVersion,
// 						reasonForRejection,
//                         }
//                         }
// `,
// 					{ clusterId: id }
// 				);
// 				if (data.cluster) {
// 					setCluster(data.cluster);
// 				}
// 			} catch (error) {
// 				console.log(error);
// 			}
// 		};

// 		fetchData();
// 	}, []);

// 	return (
// 		// <Container maxWidth={false} sx={{ padding: "24px", bgcolor: "#fafafa" }}>
// 		<Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
// 				<MiniBot/>
// 			<Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
// 				<Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
// 					<Link underline="hover" color="inherit" href="/requests/clusters">
// 						Requests
// 					</Link>
// 					<Typography color="text.primary">Request Details</Typography>
// 				</Breadcrumbs>
// 				<Box display="flex" alignItems="center" mt={1}>
// 					<IconButton
// 						edge="start"
// 						aria-label="back"
// 						color="inherit"
// 						onClick={() => navigate("/requests/clusters")}
// 					>
// 						<ArrowBackIosNewIcon fontSize="small" />
// 					</IconButton>
// 					<Typography fontFamily="Gilroy-Bold" fontSize="18px">
// 						Request Details
// 					</Typography>
// 				</Box>
// 			</Box>
// 			<Grid container>
// 				<Grid item xs={0.5} />
// 				<Grid item xs={11}>
// 					{cluster.id && (
// 						<Formik
// 							initialValues={{
// 								clusterNamePrefix: cluster.clusterCreationType == "existing"? `${cluster.clusterName}`: `${cluster.clusterName}-${cluster.environment}`,
// 								vpcProjectId: "",
// 								network: "",
// 								nodeSubnet: "",
// 								nodeSubnetRange: "",
// 								secondaryPodRange: "",
// 								secondaryServiceRange: "",
// 								releaseChannel:  cluster.clusterCreationType == "existing"? "stable": "",
// 								kubernetesVersion: cluster.clusterCreationType == "existing"? "default": "",
// 								reasonForRejection: "",
// 							}}
// 							validationSchema={validationSchema}
// 							onSubmit={async ({
// 								clusterNamePrefix,
// 								vpcProjectId,
// 								network,
// 								nodeSubnet,
// 								nodeSubnetRange,
// 								secondaryPodRange,
// 								secondaryServiceRange,
// 								releaseChannel ,
// 								kubernetesVersion,
// 							}) => {
// 								// console.log({
// 								// 	clusterNamePrefix,
// 								// 	vpcProjectId,
// 								// 	network,
// 								// 	nodeSubnet,
// 								// 	nodeSubnetRange,
// 								// 	secondaryPodRange,
// 								// 	secondaryServiceRange,
// 								// 	releaseChannel,
// 								// 	kubernetesVersion,
// 								// });
// 								const data = await queryGraphql(
// 									`
// 													mutation ApproveCluster($input: ClusterApproveInput!) {
//   approveCluster(input: $input) {
//     id
//   }
// }
// 							`,
// 									{
// 										input: {
// 											clusterNamePrefix,
// 											id: cluster.id,
// 											kubernetesVersion : await cluster.clusterCreationType == "existing"? "default" : kubernetesVersion,
// 											network,
// 											nodeSubnet,
// 											nodeSubnetRange,
// 											releaseChannel : await cluster.clusterCreationType == "existing"? "stable" : releaseChannel,
// 											secondaryPodRange,
// 											secondaryServiceRange,
// 											vpcProjectId,
// 										},
// 									}
// 								);

// 								const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
// 									updateResourceStatus(input: $input) {
// 										id
// 									}
// 									}`, {
// 										input: {
// 										  currentStatus: "active",
// 										  resourceId: id
// 										}
// 									  });

// 								if (data.approveCluster.id && info.updateResourceStatus.id) {
// 									navigate("/requests/clusters");
// 								}
// 							}}
// 						>
// 							{({
// 								handleChange,
// 								handleBlur,
// 								values,
// 								setFieldValue,
// 								errors,
// 								touched,
// 								isValid,
// 								dirty,
// 							}) => {
// 								return (
// 									<Form id="approver-main">
									
// 										<Box mx={4}>
// 											<Typography fontSize="16px" my={1}>
// 												Review the cluster and fill the cloud infra details
// 											</Typography>
// 											<Box>
// 												<Box
// 													bgcolor={
// 														cluster.status == "active"
// 															? "#12B76A10"
// 															: cluster.status == "rejected"
// 															? "#F0443810"
// 															: "#FDB02210"
// 													}
// 													justifyContent="space-between"
// 													alignItems="center"
// 													display="flex"
// 													borderRadius="12px 12px 0px 0px"
// 													p={2}
// 													boxShadow={1}
// 												>
// 													<Box>
// 														<Typography fontSize="14px">Cluster Details</Typography>
// 														<Typography
// 															fontFamily="Gilroy-Regular"
// 															fontSize="12px"
// 														>{`Requested By: ${cluster.clusterOwner?.name} | On: ${moment(
// 															new Date(Number(cluster.createdAt)).toISOString()
// 														).format("DD/MM/YYYY")}`}</Typography>
// 													</Box>
// 													<Box alignItems="center">
// 														<Box sx={{ display: "flex", alignItems: "center" }}>
// 															<img
// 																src={
// 																	cluster.status == "active"
// 																		? "/assets/tickcircle.svg"
// 																		: cluster.status == "rejected"
// 																		? "/assets/reject.svg"
// 																		: "/assets/clock.svg"
// 																}
// 																alt="Icon"
// 																style={{ width: "20px", marginRight: "8px" }}
// 															/>
// 															<Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
// 																{cluster.status == "active"
// 																	? "Active"
// 																	: cluster.status == "rejected"
// 																	? "Rejected"
// 																	: "Pending Approval"}
// 															</Typography>
// 														</Box>
// 														{(cluster.status == "active" || cluster.status == "rejected") && (
// 															<Typography
// 																fontSize="12px"
// 																fontFamily="Gilroy-Regular"
// 															>{`On: ${moment(
// 																new Date(Number(cluster.actionedAt)).toISOString()
// 															).format("DD/MM/YYYY")}`}</Typography>
// 														)}
// 													</Box>
// 												</Box>

// 												<Box bgcolor="#fff" p={2} borderRadius="0px 0px 12px 12px" boxShadow={1}>
// 													<Grid container>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Cluster Name
// 																</Typography>
// 																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																	{cluster.clusterName}
// 																</Typography>
// 															</Box>
// 														</Grid>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Cluster Owner
// 																</Typography>
// 																<Box display="flex" alignItems="center" justifyContent="flex-start">
// 																	<Avatar
// 																		sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
// 																		src={cluster.clusterOwner?.googleProfilePicture}
// 																	/>
// 																	<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																		{cluster.clusterOwner?.name}
// 																	</Typography>
// 																</Box>
// 															</Box>
// 														</Grid>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	GCP Project Id
// 																</Typography>
// 																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																	{cluster.gcpProjectId}
// 																</Typography>
// 															</Box>
// 														</Grid>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Environment
// 																</Typography>
// 																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																	{cluster.environment}
// 																</Typography>
// 															</Box>
// 														</Grid>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Region{" "}
// 																</Typography>
// 																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																	{cluster.region}
// 																</Typography>
// 															</Box>
// 														</Grid>
// 													</Grid>
// 													<Grid container mt={2}>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Cluster Size
// 																</Typography>
// 																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																	{cluster.clusterSize}
// 																</Typography>
// 															</Box>
// 														</Grid>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Cluster Type
// 																</Typography>
// 																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																	{cluster.clusterType}
// 																</Typography>
// 															</Box>
// 														</Grid>

// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Cluster Viewers
// 																</Typography>
// 																<Box display="flex" alignItems="center" justifyContent="flex-start">
// 																	{cluster.gcpClusterViewers?.map((e: string) => {
// 																		return (
// 																			<Tooltip title={e}>
// 																				<Avatar
// 																					sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
// 																					src={e}
// 																					alt={e}
// 																				/>
// 																			</Tooltip>
// 																		);
// 																	})}
// 																</Box>
// 															</Box>
// 														</Grid>
// 														<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Application Owners
// 																</Typography>
// 																<Box display="flex" alignItems="center" justifyContent="flex-start">
// 																	{cluster.applicationRequesters?.map((e: string) => {
// 																		return (
// 																			<Tooltip title={e}>
// 																				<Avatar
// 																					sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
// 																					src={e}
// 																					alt={e}
// 																				/>
// 																			</Tooltip>
// 																		);
// 																	})}
// 																</Box>
// 															</Box>
// 														</Grid>
														
// 													</Grid>
// 													<Box>
// 														<Typography
// 															gutterBottom
// 															fontFamily="Gilroy-Regular"
// 															fontSize="12px"
// 															mt={2}
// 														>
// 															Tags/ Labels
// 														</Typography>
// 														<Box ml={-0.5}>
// 															{cluster.costLabelPairs?.map((e: any) => {
// 																return (
// 																	<Chip
// 																		size="small"
// 																		sx={{
// 																			bgcolor: "#10101010",
// 																			border: "1px solid black",
// 																			mr: 0.75,
// 																			fontFamily: "Gilroy-Regular",
// 																			fontSize: "12px",
// 																		}}
// 																		variant="filled"
// 																		label={`${e.cost} • ${e.label}`}
// 																	/>
// 																);
// 															})}
// 														</Box>
// 													</Box>
// 												</Box>

// 												{cluster.status == "pending" &&
// 													user.roles.some((element: string) =>
// 														["infrastructure_admin", "viewer"].includes(element)
// 													) && (
// 														<Box mt={2} bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1}>
// 															<Typography fontSize="14px">Cloud Infra Details</Typography>
// 															<Divider sx={{ marginY: 2 }} />
// 															<Grid container gap={2}>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Cluster Name Prefix" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["clusterNamePrefix"] &&
// 																					touched["clusterNamePrefix"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		disabled
// 																		name="clusterNamePrefix"
// 																		value={values.clusterNamePrefix}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter name here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="clusterNamePrefix" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="VPC-GCP Project ID" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["vpcProjectId"] && touched["vpcProjectId"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		name="vpcProjectId"
// 																		value={values.vpcProjectId}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter name here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="vpcProjectId" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Network" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["network"] && touched["network"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		name="network"
// 																		value={values.network}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter name here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="network" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Node Subnet" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["nodeSubnet"] && touched["nodeSubnet"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		name="nodeSubnet"
// 																		value={values.nodeSubnet}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter name here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="nodeSubnet" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Master CIDR Range" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["nodeSubnetRange"] && touched["nodeSubnetRange"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		name="nodeSubnetRange"
// 																		value={values.nodeSubnetRange}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter CIDR here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="nodeSubnetRange" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Secondary Range Name for Pods" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["secondaryPodRange"] &&
// 																					touched["secondaryPodRange"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		name="secondaryPodRange"
// 																		value={values.secondaryPodRange}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter name here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="secondaryPodRange" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Secondary Range Name for Services" />
// 																	<TextField
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["secondaryServiceRange"] &&
// 																					touched["secondaryServiceRange"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",

// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		variant="filled"
// 																		fullWidth
// 																		name="secondaryServiceRange"
// 																		value={values.secondaryServiceRange}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		placeholder="Enter name here"
// 																		size="medium"
// 																		hiddenLabel
// 																		InputProps={{
// 																			disableUnderline: true,

// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	/>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="secondaryServiceRange" component="div" />
// 																	</div>
// 																</Grid>
// 																{cluster.clusterCreationType !== "existing" && (
// 																<>
// 																<Grid item xs={5.75}>
// 																	<CompulsoryFieldsLabel fieldName="Release Channel" />
// 																	<TextField
// 																		select
// 																		placeholder="Select"
// 																		name="releaseChannel"
// 																		value={values.releaseChannel}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		variant="filled"
// 																		fullWidth
// 																		hiddenLabel
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["releaseChannel"] && touched["releaseChannel"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",
// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		size="medium"
// 																		InputProps={{
// 																			disableUnderline: true,
// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	>
// 																		{releaseChannelList.map((e) => (
// 																			<MenuItem
// 																				value={e}
// 																				onClick={() => setFieldValue("releaseChannel", e)}
// 																			>
// 																				{e}
// 																			</MenuItem>
// 																		))}
// 																	</TextField>
// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="releaseChannel" component="div" />
// 																	</div>
// 																</Grid>
// 																<Grid item xs={5.75}>
// 																	<Box sx={{display:'flex'}}>
// 																	<CompulsoryFieldsLabel fieldName="Kubernetes Version" />
// 																	<Avatar
// 																		src="/assets/info.svg"
// 																		alt="info"
// 																		sx={{ height: 18, width: 18, marginLeft: "4px" , pt: 0.2, cursor: 'pointer'}}
// 																		onClick= {() => {window.open('https://cloud.google.com/kubernetes-engine/docs/release-notes-stable', '_blank')}}
// 																	/>
																	
// 																	</Box>
// 																 {/* <TextField
// 																		select
// 																		placeholder="Select"
// 																		name="kubernetesVersion"
// 																		value={values.kubernetesVersion}
// 																		onChange={handleChange}
// 																		onBlur={handleBlur}
// 																		variant="filled"
// 																		fullWidth
// 																		hiddenLabel
// 																		sx={{
// 																			"& .MuiFilledInput-root": {
// 																				border:
// 																					errors["kubernetesVersion"] &&
// 																					touched["kubernetesVersion"]
// 																						? "1px solid red"
// 																						: "1px solid #00000000",
// 																				"&.Mui-focused": {
// 																					backgroundColor: "white",
// 																					border: "1px solid black",
// 																				},
// 																			},
// 																		}}
// 																		size="medium"
// 																		InputProps={{
// 																			disableUnderline: true,
// 																			style: {
// 																				borderRadius: "8px",
// 																			},
// 																		}}
// 																	>
// 																		{(values.releaseChannel &&
// 																		kubernetesVersionList[values.releaseChannel]
// 																			? kubernetesVersionList[values.releaseChannel]
// 																			: kubernetesVersionList["All"]
// 																		).map((e: string) => (
// 																			<MenuItem
// 																				value={e}
// 																				onClick={() => setFieldValue("kubernetesVersion", e)}
// 																			>
// 																				{e}
// 																			</MenuItem>
// 																		))}
// 																	</TextField>  */}
																	
// 											{/* New component to accept user input as well as drop down */}
// 														<Autocomplete
// 															freeSolo
// 															onChange={(event, newValue) => {
// 																setFieldValue('kubernetesVersion', newValue);
// 															  }}
// 															value={values.kubernetesVersion}
// 															options={values.releaseChannel &&
// 																kubernetesVersionList[values.releaseChannel]
// 																	? kubernetesVersionList[values.releaseChannel]
// 																	: kubernetesVersionList["All"]}
// 															filterSelectedOptions
// 															renderInput={(params) => (
// 																<TextField
// 																	{...params}
// 																	name= "kubernetesVersion"
// 																	onChange={handleChange}
// 																	onBlur={handleBlur}
// 																	value={values.kubernetesVersion}
// 																	variant="filled"
// 																	fullWidth
// 																	placeholder="Enter Kubernetes version"
// 																	size="medium"
// 																	hiddenLabel
// 																	sx={{
// 																		"& .MuiFilledInput-root": {
// 																			border:
// 																				errors["kubernetesVersion"] && touched["kubernetesVersion"]
// 																					? "1px solid red"
// 																					: "1px solid #00000000",
// 																			"&.Mui-focused": {
// 																				backgroundColor: "white",
// 																				border: "1px solid black",
// 																			},
// 																		},
// 																	}}
// 																	InputProps={{
// 																		...params.InputProps,
// 																		style: {
// 																			borderRadius: "8px",
// 																		},

// 																		disableUnderline: true,
// 																	}}
// 																/>
																
// 															)}
// 														/>
// 														 {/* Selected Value - {values.kubernetesVersion} */}


// 																	<div
// 																		style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}
// 																	>
// 																		<ErrorMessage name="kubernetesVersion" component="div" />
// 																	</div>
// 																</Grid>
// 																</>
// 															)}
// 															</Grid>
// 														</Box>
// 													)}
// 											</Box>
// 											{(cluster.status == "active" || cluster.status == "rejected") && (
// 												<Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
// 													<Typography fontSize="14px">Cloud Infra Details</Typography>
// 													<Divider sx={{ my: 2 }} />
// 													{cluster.status == "rejected" && (
// 														<Grid container>
// 															<Grid item xs={2.4}>
// 																<Box>
// 																	<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																		Reason for rejection
// 																	</Typography>
// 																	<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																		{cluster.reasonForRejection}
// 																	</Typography>
// 																</Box>
// 															</Grid>
// 														</Grid>
// 													)}
// 													{cluster.status == "active" && (
// 														<>
// 															<Grid container>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Cluster Name Prefix
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.clusterNamePrefix}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			VPC Project ID
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.vpcProjectId}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Network
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.network}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Node Subnet
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.nodeSubnet}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Master CIDR range
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.nodeSubnetRange}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 															</Grid>
// 															<Grid container mt={2}>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Secondary Range Name for Pods
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.secondaryPodRange}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Secondary Range Name for Services
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.secondaryServiceRange}
// 																		</Typography>
// 																	</Box>
// 																</Grid>

// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Release Channel
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.releaseChannel}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 																	<Box>
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																			Kubernetes Version
// 																		</Typography>
// 																		<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
// 																			{cluster.kubernetesVersion}
// 																		</Typography>
// 																	</Box>
// 																</Grid>
// 																<Grid item xs={2.4}>
// 															<Box>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																	Infra URLs
// 																</Typography>
// 																<Box display="flex" alignItems="center" justifyContent="flex-start">
// 																	<Typography fontFamily="Gilroy-SemiBold" fontSize={14} sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' , display:"flex", alignItems: "center"}}>
// 														5
// 														<Tooltip 
// 															sx={{ whiteSpace: "pre-line" }}
// 															// title={cluster.applications?.map(
// 															// 	(e: any, index: number) => `${e.applicationName}`
// 															// )}
// 															title={
// 																<>
// 																<Typography fontFamily="Gilroy-Regular" fontSize="14px" fontWeight={700}>
			
// 																	List of Infra URLs
// 																</Typography>
// 																	<br />
// 																	{resourceStatus.resourceId === id && resourceStatus.gcpClusterUrl ? (
// 																		<Link
// 																			href={resourceStatus.gcpClusterUrl}
// 																			target="_blank"
// 																			rel="noopener noreferrer"
// 																			sx={{
// 																				textDecoration: 'underline', // Ensure underline for link appearance
// 																				color: 'inherit' // Inherit color from Typography, or set explicitly if needed
// 																			}}
// 																		>
// 																			<Typography
// 																				fontFamily="Gilroy-Bold"
// 																				fontSize="12px"
// 																				// Apply link color here if 'inherit' doesn't work as expected,
// 																				// or if you want a specific link color different from default Typography.
// 																				// For example: color: '#ADD8E6' (light blue)
// 																			>
// 																				Cluster URL
// 																			</Typography>
// 																		</Link>
// 																	) : (
// 																		// If no link available (loading state), display the label as plain text
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																						Cluster URL
																	
// 																		</Typography>
// 																	)}
			
// 																	{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 															Cluster URL
// 														</Typography>
// 														<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
// 															{resourceStatus.resourceId === id ? (
// 																<Link href={resourceStatus.gcpClusterUrl} 
// 																target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: '#FFFFFF' }}> 
// 																{`${resourceStatus.gcpClusterUrl.substring(0,10)}...`} 
// 																</Link>
// 																		) : (
// 																	<>Loading...</>
// 																			)}
// 																</Typography> */}
			
// 																	<br/>
// 																	{resourceStatus.resourceId === id && resourceStatus.projectUrl ? (
// 																		<Link
// 																			href={resourceStatus.projectUrl}
// 																			target="_blank"
// 																			rel="noopener noreferrer"
// 																			sx={{
// 																				textDecoration: 'underline', // Ensure underline for link appearance
// 																				color: 'inherit' // Inherit color from Typography, or set explicitly if needed
// 																			}}
// 																		>
// 																			<Typography
// 																				fontFamily="Gilroy-Bold"
// 																				fontSize="12px"
// 																				// Apply link color here if 'inherit' doesn't work as expected,
// 																				// or if you want a specific link color different from default Typography.
// 																				// For example: color: '#ADD8E6' (light blue)
// 																			>
// 																				Project URL
// 																			</Typography>
// 																		</Link>
// 																	) : (
// 																		// If no link available (loading state), display the label as plain text
// 																		<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																						Project URL
																	
// 																		</Typography>
// 																	)}
			
// 																	{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 															Project URL
// 														</Typography>
			
// 														<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
// 															{resourceStatus.resourceId === id ? (
// 																<Link href={resourceStatus.projectUrl} 
// 																target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline',color: '#FFFFFF' }}> 
// 																{`${resourceStatus.projectUrl.substring(0,10)}...`}
																
// 																	</Link>
// 																		) : (
// 																	<>Loading...</>
// 																			)}
// 																</Typography>
// 			 */}
// 																<br/>
// 																{resourceStatus.resourceId === id && resourceStatus.argoCdStaticUrl ? (
// 																	<Link
// 																		href={resourceStatus.argoCdStaticUrl}
// 																		target="_blank"
// 																		rel="noopener noreferrer"
// 																		sx={{
// 																			textDecoration: 'underline', // Ensure underline for link appearance
// 																			color: 'inherit' // Inherit color from Typography, or set explicitly if needed
// 																		}}
// 																	>
// 																		<Typography
// 																			fontFamily="Gilroy-Bold"
// 																			fontSize="12px"
// 																			// Apply link color here if 'inherit' doesn't work as expected,
// 																			// or if you want a specific link color different from default Typography.
// 																			// For example: color: '#ADD8E6' (light blue)
// 																		>
// 																			ArgoCD URL
// 																		</Typography>
// 																	</Link>
// 																) : (
// 																	// If no link available (loading state), display the label as plain text
// 																	<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																		ArgoCD URL
// 																	</Typography>
// 																)}
			
// 																{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 															ArgoCD URL
// 														</Typography>
// 														<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
// 															{resourceStatus.resourceId === id ? (
// 																<Link href={resourceStatus.argoCdStaticUrl} 
// 																target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline',color: '#FFFFFF' }}> 
// 																{`${resourceStatus.argoCdStaticUrl.substring(0,10)}...`} 
																
// 																</Link>
// 																		) : (
// 																	<>Loading...</>
// 																			)}
// 																</Typography> */}
			
// 																<br/>
// 																{resourceStatus.resourceId === id && resourceStatus.subGroupProjectAppUrl ? (
// 																	<Link
// 																		href={resourceStatus.subGroupProjectAppUrl}
// 																		target="_blank"
// 																		rel="noopener noreferrer"
// 																		sx={{
// 																			textDecoration: 'underline', // Ensure underline for link appearance
// 																			color: 'inherit' // Inherit color from Typography, or set explicitly if needed
// 																		}}
// 																	>
// 																		<Typography
// 																			fontFamily="Gilroy-Bold"
// 																			fontSize="12px"
// 																			// Apply link color here if 'inherit' doesn't work as expected,
// 																			// or if you want a specific link color different from default Typography.
// 																			// For example: color: '#ADD8E6' (light blue)
// 																		>
// 																			Sub Group Project App URL
// 																		</Typography>
// 																	</Link>
// 																) : (
// 																	// If no link available (loading state), display the label as plain text
// 																	<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																		Sub Group Project App URL
// 																	</Typography>
// 																)}
			
// 																{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 															Sub Group Project App URL
// 														</Typography>
// 														<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
// 															{resourceStatus.resourceId === id ? (
// 																<Link href={resourceStatus.subGroupProjectAppUrl} 
// 																target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: '#FFFFFF' }}> 
// 																{`${resourceStatus.subGroupProjectAppUrl.substring(0,10)}...`} 
// 																</Link>
			
// 																		) : (
// 																	<>Loading...</>
// 																			)}
// 																</Typography> */}
			
// 																<br/>
// 																{resourceStatus.resourceId === id && resourceStatus.subGroupProjectInfraUrl ? (
// 																	<Link
// 																		href={resourceStatus.subGroupProjectInfraUrl}
// 																		target="_blank"
// 																		rel="noopener noreferrer"
// 																		sx={{
// 																			textDecoration: 'underline', // Ensure underline for link appearance
// 																			color: 'inherit' // Inherit color from Typography, or set explicitly if needed
// 																		}}
// 																	>
// 																		<Typography
// 																			fontFamily="Gilroy-Bold"
// 																			fontSize="12px"
// 																			// Apply link color here if 'inherit' doesn't work as expected,
// 																			// or if you want a specific link color different from default Typography.
// 																			// For example: color: '#ADD8E6' (light blue)
// 																		>
// 																			Sub Group Project Infra URL
// 																		</Typography>
// 																	</Link>
// 																) : (
// 																	// If no link available (loading state), display the label as plain text
// 																	<Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 																					Sub Group Project Infra URL
																
// 																	</Typography>
// 																)}
			
// 																{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
// 															Sub Group Project Infra URL
// 														</Typography>
// 														<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
// 															{resourceStatus.resourceId === id ? (
			
// 													<Link href={resourceStatus.subGroupProjectInfraUrl} 
// 														target="_blank" rel="noopener noreferrer" sx={{textDecoration: 'underline', color: '#FFFFFF' }}> 
// 														{`${resourceStatus.subGroupProjectInfraUrl.substring(0,10)}...`} 
													
// 														</Link>
			
// 																		) : (
// 																	<>Loading...</>
// 																			)}
// 																</Typography>
// 			 */}
			
// 																</>
// 															}
// 															slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
// 														>
// 															<Avatar
// 																src="/assets/info.svg"
// 																alt="info"
// 																sx={{ height: 16, width: 16, marginLeft: "4px" }}
// 															/>
															
// 														</Tooltip>
// 																</Typography>
// 															</Box>
// 															</Box>
// 														</Grid>
// 															</Grid>
// 														</>
// 													)}
// 												</Box>
// 											)}
// 											{cluster.status == "pending" &&
// 												user.roles.some((element: string) =>
// 													["infrastructure_admin"].includes(element)
// 												) && (
// 													<Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
// 														<Button
// 															sx={{
// 																color: "black",
// 																borderColor: "black",
// 																fontWeight: "bold",
// 																textTransform: "none",
// 																px: 4,
// 																fontFamily: "Gilroy-Bold",
// 																fontSize: "14px",
// 															}}
// 															variant="outlined"
// 															size="large"
// 															onClick={() => {
// 																setOpenRejectModal(true);
// 															}}
// 														>
// 															Reject Request
// 														</Button>
// 														<Button
// 															sx={{
// 																backgroundColor: "black",
// 																color: "white",
// 																fontWeight: "bold",
// 																"&:hover": {
// 																	backgroundColor: "white",
// 																	color: "black",
// 																	borderColor: "black",
// 																},
// 																textTransform: "none",
// 																px: 4,
// 																fontFamily: "Gilroy-Bold",
// 																fontSize: "14px",
// 															}}
// 															variant="contained"
// 															size="large"
// 															// type="submit"
// 															disabled={!(isValid && dirty)}
// 															onClick={() => {
// 																setOpenApproveModal(true);
// 															}}
// 														>
// 															Approve Request
// 														</Button>
// 													</Box>
// 												)}
// 											<Modal
// 												open={openApproveModal}
// 												onClose={(event, reason): any => {
// 													// if (reason && reason === "backdropClick") return;
// 													// setOpenRejectModal(false);
// 												}}
// 											>
// 												<Box
// 													sx={{
// 														position: "absolute",
// 														top: "50%",
// 														left: "50%",
// 														transform: "translate(-50%, -50%)",
// 														width: 700,
// 														bgcolor: "white",
// 														border: "0px",
// 														boxShadow: 2,
// 														borderRadius: "16px",
// 													}}
// 												>
// 													<Box display="flex" justifyContent="space-between">
// 														<Typography fontFamily="Gilroy-SemiBold" fontSize="20px" p={2}>
// 															Approve
// 														</Typography>
// 														<IconButton
// 															edge="start"
// 															aria-label="back"
// 															color="inherit"
// 															onClick={() => setOpenApproveModal(false)}
// 															sx={{ m: 2 }}
// 														>
// 															<Close fontSize="small" />
// 														</IconButton>
// 													</Box>
// 													<Divider />
// 													<Typography fontSize="16px" p={2}>
// 														Are you sure you want to approve the request?
// 													</Typography>
// 													<Typography fontSize="16px" p={2}>
// 														On approving request will be approved and Kubernetes cluster details
// 														will be added successful.
// 													</Typography>
// 													<Divider />
// 													<Box sx={{ m: 2, textAlign: "right" }}>
// 														<Button
// 															sx={{
// 																backgroundColor: "black",
// 																color: "white",
// 																fontWeight: "bold",
// 																"&:hover": {
// 																	backgroundColor: "white",
// 																	color: "black",
// 																	borderColor: "black",
// 																},
// 																textTransform: "none",
// 																px: 4,
// 																fontFamily: "Gilroy-Bold",
// 																fontSize: "14px",
// 															}}
// 															variant="contained"
// 															size="small"
// 															type="submit"
// 															form="approver-main"
// 														>
// 															Approve
// 														</Button>
// 													</Box>
// 												</Box>
// 											</Modal>
// 										</Box>
// 									</Form>
// 								);
// 							}}
// 						</Formik>
// 					)}
// 				</Grid>
// 				<Grid item xs={0.5} />
// 			</Grid>

// 			<Modal
// 				open={openRejectModal}
// 				onClose={(event, reason): any => {
// 					// if (reason && reason === "backdropClick") return;
// 					// setOpenRejectModal(false);
// 				}}
// 			>
// 				<Box
// 					sx={{
// 						position: "absolute",
// 						top: "50%",
// 						left: "50%",
// 						transform: "translate(-50%, -50%)",
// 						width: 700,
// 						bgcolor: "white",
// 						border: "0px",
// 						boxShadow: 2,
// 						borderRadius: "16px",
// 					}}
// 				>
// 					<Formik
// 						onSubmit={async ({ reasonForRejection }) => {
// 							// console.log({
// 							// 	reasonForRejection,
// 							// });
// 							const data = await queryGraphql(
// 								`
// 														mutation RejectCluster($input: ClusterRejectInput!) {
// 														rejectCluster(input: $input) {
// 															id
// 														}
// }
// 								`,
// 								{
// 									input: {
// 										id: cluster.id,
// 										reasonForRejection,
// 									},
// 								}
// 							);

// 							const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
// 								updateResourceStatus(input: $input) {
// 									id
// 								}
// 								}`, {
// 									input: {
// 									  currentStatus: "rejected",
// 									  resourceId: id
// 									}
// 								  });

// 							if (data.rejectCluster.id && info.updateResourceStatus.id) {
// 								navigate("/requests/clusters");
// 							}
// 						}}
// 						validationSchema={Yup.object().shape({
// 							reasonForRejection: Yup.string()
// 								.required("This field is required")
// 								.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 								.strict(),
// 						})}
// 						initialValues={{ reasonForRejection: "" }}
// 					>
// 						{({
// 							handleChange,
// 							handleBlur,
// 							values,
// 							setFieldValue,
// 							errors,
// 							touched,
// 							isValid,
// 							dirty,
// 						}) => {
// 							return (
// 								<Form id="reject-form">
// 									<Box display="flex" justifyContent="space-between">
// 										<Typography fontFamily="Gilroy-SemiBold" fontSize="20px" p={2}>
// 											Reject Request
// 										</Typography>
// 										<IconButton
// 											edge="start"
// 											aria-label="back"
// 											color="inherit"
// 											onClick={() => setOpenRejectModal(false)}
// 											sx={{ m: 2 }}
// 										>
// 											<Close fontSize="small" />
// 										</IconButton>
// 									</Box>
// 									<Divider />
// 									<Typography fontSize="16px" p={2}>
// 										Add comments for the requestor explaining the reason of rejection
// 									</Typography>
// 									<Box p={2}>
// 										<CompulsoryFieldsLabel fieldName="Comments" />
// 										<TextField
// 											sx={{
// 												"& .MuiFilledInput-root": {
// 													border:
// 														errors["reasonForRejection"] && touched["reasonForRejection"]
// 															? "1px solid red"
// 															: "1px solid #00000000",

// 													"&.Mui-focused": {
// 														backgroundColor: "white",
// 														border: "1px solid black",
// 													},
// 												},
// 											}}
// 											variant="filled"
// 											fullWidth
// 											multiline
// 											name="reasonForRejection"
// 											value={values.reasonForRejection}
// 											onChange={handleChange}
// 											onBlur={handleBlur}
// 											placeholder="Enter comment"
// 											size="medium"
// 											hiddenLabel
// 											InputProps={{
// 												disableUnderline: true,

// 												style: {
// 													borderRadius: "8px",
// 												},
// 											}}
// 										/>
// 										<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
// 											<ErrorMessage name="reasonForRejection" component="div" />
// 										</div>
// 									</Box>

// 									<Box sx={{ m: 2, textAlign: "right" }}>
// 										<Button
// 											sx={{
// 												backgroundColor: "black",
// 												color: "white",
// 												fontWeight: "bold",
// 												"&:hover": {
// 													backgroundColor: "white",
// 													color: "black",
// 													borderColor: "black",
// 												},
// 												textTransform: "none",
// 												px: 4,
// 												fontFamily: "Gilroy-Bold",
// 												fontSize: "14px",
// 											}}
// 											variant="contained"
// 											size="small"
// 											type="submit"
// 											form="reject-form"
// 											disabled={!(isValid && dirty)}
// 										>
// 											Reject
// 										</Button>
// 									</Box>
// 								</Form>
// 							);
// 						}}
// 					</Formik>
// 				</Box>
// 			</Modal>
// 		</Container>
// 	);
// };

// //export default ClusterApproveFormPage;

// const ClusterApproveFormPage: React.FC = () => {
//     const theme = useTheme();

//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//     };

//     // Using your remembered calculation for marginLeft
//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 // Assuming "integrations" is the correct index for Kyverno page
//                 // If Kyverno has its own top-level menu item, update this index.
//                 index={"requests-cluster"} // Or a more specific index like "integrations-kyverno" if applicable
//                 onStateChange={handleDrawerStateChange}
//             />
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
//                     p: 0,
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     // Using your remembered unit and calculation for marginLeft
//                     marginLeft: `${currentMarginLeft}vh`,
//                 }}
//             >
//                 <ClusterApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default ClusterApproveFormPage;